/**
 * Audit UI 단순 i18n — 라이브러리 없이 dictionary lookup.
 *
 * 사용:
 *   import { useLang } from "./i18n";
 *   const { t, lang, setLang } = useLang();
 *   <h1>{t("page.title")}</h1>
 *
 * 언어: ko (default) / en. localStorage 'audit_lang' 보존.
 * 신규 페이지에서 항목 누락 시 key 그대로 반환 (development noise).
 */
import { useEffect, useState, useCallback } from "react";

const LANG_KEY = "audit_lang";
const SUPPORTED = ["ko", "en"];

const messages = {
  ko: {
    // 공통
    "common.main": "Hub",
    "common.reset": "초기화",
    "common.help": "도움말",
    "common.close": "닫기",
    "common.cancel": "취소",
    "common.save": "저장",
    "common.delete": "삭제",
    "common.add": "추가",
    "common.edit": "수정",
    "common.error": "오류",
    "common.loading": "로딩 중...",
    "common.all": "전체",
    "common.cases": "건",

    // PrecedentsPage 상단 바
    "precedents.title": "관세청 품목분류 사례",
    "precedents.search.placeholder": '예: 조제효소 건조 -농축  "GRI 3 가목"',
    "precedents.search.title":
      '다중 검색 문법:\n  공백 = AND (모두 포함)\n  "..." = 정확한 구문\n  -단어 = 제외\n예) 조제효소 건조 -농축 "GRI 3 가목"',
    "precedents.history.recent": "최근 검색",
    "precedents.ai.classify": "AI 분류",
    "precedents.ai.title":
      "AI 분류 어시스턴트 — line item 입력 → HSK 분류 + 매치 선례",
    "precedents.columns": "컬럼",
    "precedents.columns.title": "컬럼 표시 / 숨김 — localStorage 보존",
    "precedents.columns.heading": "그리드 컬럼",
    "precedents.columns.required": "필수",
    "precedents.columns.restore": "기본값 복원",
    "precedents.columns.showAll": "전체 표시",
    "precedents.bookmarks.only": "즐겨찾기 {n}건만 보기 (토글)",
    "precedents.bookmarks.empty":
      "즐겨찾기 없음 — 그리드 ★ 컬럼 또는 우측 패널 ★ 버튼으로 추가",
    "precedents.csv.title": "현재 결과 {n}건 CSV 다운로드 (Excel 한글 BOM)",
    "precedents.tree.search.placeholder": "류 이름 검색 (예: 가죽, 신발)",
    "precedents.tree.title": "부 · 류 트리",
    "precedents.grid.helper":
      "컬럼 헤더 필터 (≡) 로 다중 필터 · 컬럼 클릭으로 정렬 · 키워드 입력 시 매치 부분 노란색 마커",
    "precedents.scroll.label": "가로 점프:",
    "precedents.scroll.start": "처음 (부·류)",
    "precedents.scroll.mid": "가운데 (HSK)",
    "precedents.scroll.end": "끝 (품명·출처)",
    "precedents.scroll.hint": "그리드 하단 스크롤바 대신 클릭 한 번",
    "precedents.detail.empty": "결과 없음 — 트리/키워드를 변경하세요",
    "precedents.detail.title": "품명",
    "precedents.detail.description": "물품 설명",
    "precedents.detail.reasoning": "결정 사유",
    "precedents.detail.source": "출처",
    "precedents.detail.original": "원문",
    "precedents.detail.linkText": "링크",
    "precedents.detail.section": "부 · 류",
    "precedents.detail.hsk": "HSK",
    "precedents.detail.date": "시행일",
    "precedents.bookmark.add": "즐겨찾기 추가",
    "precedents.bookmark.remove": "즐겨찾기 해제",
    "precedents.bookmark.saved": "저장됨",
    "precedents.bookmark.unsaved": "저장",
    "precedents.loading.cases": "로딩 중... (12,606건)",

    // SearchHelpModal
    "help.title": "분류사례 검색 도움말",
    "help.s1.title": "1) 키워드 문법 (Google 풍)",
    "help.s1.col.syntax": "문법",
    "help.s1.col.meaning": "의미",
    "help.s1.col.example": "예",
    "help.s1.row1.syntax": "공백 (단어 두 개+)",
    "help.s1.row1.meaning": "AND (모두 포함)",
    "help.s1.row2.syntax": '"..." (따옴표)',
    "help.s1.row2.meaning": "정확한 구문",
    "help.s1.row3.syntax": "-단어 (마이너스)",
    "help.s1.row3.meaning": "제외 (NOT)",
    "help.s1.note":
      "매치 토큰은 본문 + 그리드 셀에 색 마커로 표시: 사용자 검색어 = 7색 (노란/녹/청...), 동의어 자동 expansion = 연보라 (예 'handbag' 검색 시 '핸드백/가방/bag' 자동 매치).",
    "help.s2.title": "2) 검색 동작 방식 (자동 hybrid)",
    "help.s2.col.off": "OFF (빠른 검색)",
    "help.s2.col.on": "ON (정밀 검색)",
    "help.s2.method": "방식",
    "help.s2.speed": "속도",
    "help.s2.synonym": "동의어",
    "help.s2.morpheme": "형태소",
    "help.s2.example": "매치 예",
    "help.s2.note":
      "FTS (pgroonga 형태소 + 동의어) 와 vector (Gemini embedding cosine) 를 RRF 로 융합 — 토글 없이 자동. 영문 query 가 한글 사례 매치 (handbag → 핸드백/가방).",
    "help.s3.title": "3) 필터 조합 (다중)",
    "help.s3.tree":
      "좌측 트리 (부·류): 클릭으로 카테고리 좁히기. 누적 카운트 표시.",
    "help.s3.inline":
      "그리드 헤더 인라인 필터: 각 컬럼에 input 자동 노출. 부 / 류 / HS / 품명 / 사례번호 등 컬럼별 부분일치 필터.",
    "help.s3.keyword":
      "상단 키워드: 전체 본문 (품명 + 물품설명 + 결정사유) 매치.",
    "help.s3.bodycol":
      "본문 컬럼 ON: 물품설명 + 결정사유 컬럼을 그리드에 추가해 컬럼 필터로 본문 검색 (관세사 power user 용).",
    "help.s3.combine":
      "위 4개는 동시에 작동 (AND). 키워드 + 트리 + 컬럼 필터 = 매우 정밀한 좁히기.",
    "help.s4.title": "4) 키보드 단축",
    "help.s4.k1": "인라인 필터 input 에서 ↓: 그리드 첫 행으로 포커스 이동",
    "help.s4.k2": "그리드 행 0 에서 ↑: 인라인 필터로 복귀",
    "help.s4.k3": "그리드 ↑↓: 행 이동 + 우측 상세 즉시 갱신",
    "help.s4.k4": "행 더블클릭 또는 클릭: 우측 상세 패널 확인",
    "help.s5.title": "5) 편의 기능",
    "help.s5.bookmark":
      "즐겨찾기 (★): 그리드 ★ 컬럼 또는 우측 패널 [저장] 버튼으로 토글. 상단 ★ 버튼으로 즐겨찾기만 필터. 로컬에만 저장됩니다.",
    "help.s5.history":
      "검색 history: 검색 input 클릭 시 최근 10개 검색어 dropdown.",
    "help.s5.csv":
      "CSV 다운로드: 상단 CSV 버튼 = 현재 필터된 결과 그대로 (Excel 한글 BOM, 12 컬럼).",
    "help.s5.treesearch":
      "좌측 트리 빠른 검색: 트리 상단 input 에 '가죽', '신발' 등 입력 → 매치 류만 노출 + 자동 expand.",
    "help.s5.url":
      "URL 공유: 키워드 + 트리 선택은 URL 에 자동 저장 — 링크 공유 가능.",

    // AiClassifyPanel
    "ai.title": "AI 분류 어시스턴트",
    "ai.intro":
      "Line item 을 입력하면 Dual-Evidence 엔진이 HSK 10자리 분류 + 법령·선례 근거 를 제시합니다. 매치 선례 카드 클릭 시 그리드에서 해당 행으로 점프.",
    "ai.input.placeholder":
      "예: PERFUME ROSE 30ML  /  Cotton T-shirt for men  /  Leather handbag",
    "ai.chapter.placeholder": "auto",
    "ai.chapter.title": "chapter 힌트 (auto = 자동 감지)",
    "ai.classify.btn": "분류",
    "ai.classify.start": "AI 분류 시작 (Enter)",
    "ai.pipeline.running": "Dual-Evidence 파이프라인 진행 중...",
    "ai.pipeline.stages":
      "Stage 1 (속성) → Stage 2 (Ontology) → Stage 2.5 (선례) → Stage 3 (GRI)",
    "ai.precedents.heading": "매치 선례 — 클릭하여 그리드에서 보기",
    "ai.error.prefix": "오류:",
    "ai.clarification.intro": "추가 정보가 필요합니다",
    "ai.clarification.freeInput": "자유 입력 (Enter)",
    "ai.clarification.skip": "이대로 분류 (생략)",
    "ai.clarification.skipWarning":
      "추가 정보를 생략했습니다. 결과 신뢰도가 낮을 수 있습니다 (confidence: {conf}). 결과 검토 필요.",
    "ai.clarification.userSkipped": "(추가 정보 생략)",
    "ai.precision.modeLabel": "정밀 모드 (거래처 기반)",
    "ai.precision.modeGeneral": "범용",
    "ai.precision.modePrecision": "정확",
    "ai.precision.sellerLabel": "거래처 (수입 supplier)",
    "ai.precision.materialLabel": "재질",
    "ai.precision.endUseLabel": "주 용도",
    "ai.precision.sellerPlaceholder": "예: VESTAS WIND SYSTEMS A S",
    "ai.precision.materialPlaceholder": "가죽 / 직물 / 플라스틱 / ...",
    "ai.precision.endUsePlaceholder": "여성 fashion / 산업용 / ...",
    "ai.trade.exact": "거래처 매치 — 과거 {n}개 HS 신고 history",
    "ai.trade.fuzzy": "거래처 유사 매치 ({n} HS)",
    "ai.trade.firstTime": "신규 거래처 (과거 신고 없음)",
    "ai.trade.outage": "거래처 DB 일시 불가 — 범용 흐름",

    // HsHubPage (5 카드 grid)
    "hub.title": "HS 분류",
    "hub.subtitle": "세번분류 통합 워크스페이스",
    "hub.card.ai.title": "AI 분류",
    "hub.card.ai.desc":
      "Line item 입력 → HSK 10자리 분류 + 법령·선례 근거 (Dual-Evidence). 정확 모드에서 거래처 prior 활용.",
    "hub.card.unified.title": "HS 통합 검색",
    "hub.card.unified.desc":
      "법령·요건·세율 통합 검색. HS 코드/품명/도메인 키워드로 즉시 lookup.",
    "hub.card.precedents.title": "관세청 품목분류 사례",
    "hub.card.precedents.desc":
      "12,606 분류사례 hybrid 검색 (pgroonga FTS + pgvector 의미 매칭). 즐겨찾기, CSV, 동의어 자동 expansion.",
    "hub.card.synonyms.title": "동의어 사전 관리",
    "hub.card.synonyms.desc":
      "FTS 동의어 200+ entries CRUD. 관세사 도메인 어휘 직접 등록. term ↔ synonym 양방향.",
    "hub.card.overview.title": "📖 시스템 소개 + 사용법",
    "hub.card.overview.desc":
      "온톨로지 기반 HS 분류 — 4 Layer 구성, 검색 흐름, 주요 기능, 활용 시나리오 원페이지 안내.",
    "hub.card.tradePartners.title": "관세사 거래처 ontology",
    "hub.card.tradePartners.desc":
      "5 group (1005/1143/1144/1039/1033) 통관 history. 거래처별 HS 분포 + 특이성. AI 정확모드 prior.",
    "hub.card.ontoSearch.title": "Ontology 검색 + Narrowing",
    "hub.card.ontoSearch.desc":
      "line_item 키워드 → 통합 HS 후보 → 재질/형태/용도 narrow chip 으로 좁힘. 5 group 통합 5M+ rows.",
    "hub.card.history.title": "분류 이력",
    "hub.card.history.desc":
      "사용자 정정 + weak spot + 통계. 학습 데이터 출처.",
    "hub.footer":
      "각 카드 클릭으로 sub-page 진입. 메뉴 트리는 사이드바 'HS 분류' 단일 항목.",

    // SynonymsPage
    "synonyms.title": "동의어 사전 관리",
    "synonyms.total": "{n}건 ({total} 전체)",
    "synonyms.search.placeholder": "term / synonym 부분일치",
    "synonyms.domain.all": "(전체 도메인)",
    "synonyms.domain.title": "domain 필터",
    "synonyms.add.title": "새 동의어 추가",
    "synonyms.empty": "검색 결과 없음",
    "synonyms.col.term": "term",
    "synonyms.col.synonym": "synonym",
    "synonyms.col.note": "note",
    "synonyms.uncategorized": "(미분류)",
    "synonyms.required": "term + synonym 둘 다 필수",
    "synonyms.delete.confirm":
      '삭제: "{term}" ↔ "{synonym}" (id={id})\n진행하시겠습니까?',
    "synonyms.draft.term.ph": "term (한글/영문)",
    "synonyms.draft.synonym.ph": "synonym (반대 표현)",
    "synonyms.draft.domain.ph": "domain (예: 42-가죽)",
    "synonyms.draft.note.ph": "note (선택)",
  },
  en: {
    "common.main": "Hub",
    "common.reset": "Reset",
    "common.help": "Help",
    "common.close": "Close",
    "common.cancel": "Cancel",
    "common.save": "Save",
    "common.delete": "Delete",
    "common.add": "Add",
    "common.edit": "Edit",
    "common.error": "Error",
    "common.loading": "Loading...",
    "common.all": "All",
    "common.cases": "cases",

    "precedents.title": "KCS Tariff Classification Cases",
    "precedents.search.placeholder":
      'e.g.: prepared enzyme dried -concentrate  "GRI 3(a)"',
    "precedents.search.title":
      'Multi-search syntax:\n  space = AND (all required)\n  "..." = exact phrase\n  -word = exclude\nExample) prepared enzyme dried -concentrate "GRI 3(a)"',
    "precedents.history.recent": "Recent searches",
    "precedents.ai.classify": "AI Classify",
    "precedents.ai.title":
      "AI classification — input line item → HSK + matched precedents",
    "precedents.columns": "Columns",
    "precedents.columns.title": "Column visibility — stored in localStorage",
    "precedents.columns.heading": "Grid columns",
    "precedents.columns.required": "required",
    "precedents.columns.restore": "Restore defaults",
    "precedents.columns.showAll": "Show all",
    "precedents.bookmarks.only": "Show {n} bookmarked only (toggle)",
    "precedents.bookmarks.empty":
      "No bookmarks — add via ★ column or detail panel ★ button",
    "precedents.csv.title":
      "Download {n} filtered rows as CSV (Excel UTF-8 BOM)",
    "precedents.tree.search.placeholder":
      "Search chapter (e.g.: leather, footwear)",
    "precedents.tree.title": "Section · Chapter tree",
    "precedents.grid.helper":
      "Column header filter (≡) for multi-filter · click column to sort · yellow markers on keyword matches",
    "precedents.scroll.label": "Horizontal jump:",
    "precedents.scroll.start": "Start (Section/Ch)",
    "precedents.scroll.mid": "Middle (HSK)",
    "precedents.scroll.end": "End (Title/Source)",
    "precedents.scroll.hint": "Skip dragging the bottom scrollbar",
    "precedents.detail.empty": "No results — change tree filter or keyword",
    "precedents.detail.title": "Title",
    "precedents.detail.description": "Description",
    "precedents.detail.reasoning": "Reasoning",
    "precedents.detail.source": "Source",
    "precedents.detail.original": "Original",
    "precedents.detail.linkText": "Link",
    "precedents.detail.section": "Section · Chapter",
    "precedents.detail.hsk": "HSK",
    "precedents.detail.date": "Decision date",
    "precedents.bookmark.add": "Add bookmark",
    "precedents.bookmark.remove": "Remove bookmark",
    "precedents.bookmark.saved": "Saved",
    "precedents.bookmark.unsaved": "Save",
    "precedents.loading.cases": "Loading... (12,606 cases)",

    // SearchHelpModal
    "help.title": "Classification Cases Search Help",
    "help.s1.title": "1) Keyword syntax (Google-style)",
    "help.s1.col.syntax": "Syntax",
    "help.s1.col.meaning": "Meaning",
    "help.s1.col.example": "Example",
    "help.s1.row1.syntax": "Space (2+ words)",
    "help.s1.row1.meaning": "AND (all required)",
    "help.s1.row2.syntax": '"..." (quoted)',
    "help.s1.row2.meaning": "Exact phrase",
    "help.s1.row3.syntax": "-word (minus)",
    "help.s1.row3.meaning": "Exclude (NOT)",
    "help.s1.note":
      "Match tokens are color-marked: user query = 7-color (yellow/green/blue/...), synonym auto-expansion = light purple (e.g. 'handbag' search auto-matches '핸드백/가방/bag').",
    "help.s2.title": "2) Search behavior (auto hybrid)",
    "help.s2.col.off": "OFF (fast)",
    "help.s2.col.on": "ON (precise)",
    "help.s2.method": "Method",
    "help.s2.speed": "Speed",
    "help.s2.synonym": "Synonyms",
    "help.s2.morpheme": "Morpheme",
    "help.s2.example": "Match example",
    "help.s2.note":
      "pgroonga FTS (morpheme + synonyms) + vector (Gemini embedding cosine) fused with RRF — no toggle, automatic. English query matches Korean cases (handbag → 핸드백/가방).",
    "help.s3.title": "3) Filter composition (multiple)",
    "help.s3.tree":
      "Left tree (Section · Chapter): click to narrow category. Cumulative counts shown.",
    "help.s3.inline":
      "Grid header inline filter: each column has auto input. Section / Chapter / HS / Title / Case ID partial-match filter.",
    "help.s3.keyword":
      "Top keyword: matches across full body (title + description + reasoning).",
    "help.s3.bodycol":
      "Show body columns: add description + reasoning to grid for column-level filtering (power user).",
    "help.s3.combine":
      "All four work together (AND). Keyword + tree + column filter = very precise narrowing.",
    "help.s4.title": "4) Keyboard shortcuts",
    "help.s4.k1": "Inline filter input + ↓: jump focus to first grid row",
    "help.s4.k2": "Grid row 0 + ↑: back to inline filter",
    "help.s4.k3": "Grid ↑↓: row navigation + detail panel updates",
    "help.s4.k4": "Row click / double-click: open detail panel",
    "help.s5.title": "5) Convenience features",
    "help.s5.bookmark":
      "Bookmarks (★): toggle via grid ★ column or detail panel [Save]. Top ★ button filters to bookmarks only. Local-only storage.",
    "help.s5.history":
      "Search history: focus search input to see recent 10 searches dropdown.",
    "help.s5.csv":
      "CSV download: top CSV button = current filtered rows (Excel UTF-8 BOM, 12 columns).",
    "help.s5.treesearch":
      "Tree quick search: type 'leather' or 'footwear' in the tree input — only matched chapters shown + auto-expanded.",
    "help.s5.url":
      "URL sharing: keyword + tree selection auto-saved to URL — link-shareable.",

    // AiClassifyPanel
    "ai.title": "AI Classification Assistant",
    "ai.intro":
      "Enter a line item and the Dual-Evidence engine returns HSK 10-digit classification + legal & precedent grounds. Click a matched precedent card to jump to that grid row.",
    "ai.input.placeholder":
      "e.g.: PERFUME ROSE 30ML  /  Cotton T-shirt for men  /  Leather handbag",
    "ai.chapter.placeholder": "auto",
    "ai.chapter.title": "Chapter hint (auto = auto-detect)",
    "ai.classify.btn": "Classify",
    "ai.classify.start": "Start AI classification (Enter)",
    "ai.pipeline.running": "Dual-Evidence pipeline in progress...",
    "ai.pipeline.stages":
      "Stage 1 (Attribute) → Stage 2 (Ontology) → Stage 2.5 (Precedent) → Stage 3 (GRI)",
    "ai.precedents.heading": "Matched precedents — click to view in grid",
    "ai.error.prefix": "Error:",
    "ai.clarification.intro": "Additional information needed",
    "ai.clarification.freeInput": "Free input (Enter)",
    "ai.clarification.skip": "Classify anyway (skip)",
    "ai.clarification.skipWarning":
      "Additional info was skipped. Confidence may be low (confidence: {conf}). Review required.",
    "ai.clarification.userSkipped": "(skipped)",
    "ai.precision.modeLabel": "Precision mode (partner-based)",
    "ai.precision.modeGeneral": "General",
    "ai.precision.modePrecision": "Precision",
    "ai.precision.sellerLabel": "Trade partner (importer supplier)",
    "ai.precision.materialLabel": "Material",
    "ai.precision.endUseLabel": "End use",
    "ai.precision.sellerPlaceholder": "e.g.: VESTAS WIND SYSTEMS A S",
    "ai.precision.materialPlaceholder": "leather / textile / plastic / ...",
    "ai.precision.endUsePlaceholder": "women fashion / industrial / ...",
    "ai.trade.exact": "Partner match — {n} past HS declarations",
    "ai.trade.fuzzy": "Partner fuzzy match ({n} HS)",
    "ai.trade.firstTime": "New partner (no past declarations)",
    "ai.trade.outage": "Partner DB temporarily unavailable — general flow",

    // HsHubPage (5 cards grid)
    "hub.title": "HS Classification",
    "hub.subtitle": "Unified workspace",
    "hub.card.ai.title": "AI Classify",
    "hub.card.ai.desc":
      "Line item → HSK 10-digit + legal/precedent grounds (Dual-Evidence). Precision mode uses partner prior.",
    "hub.card.unified.title": "Unified Search",
    "hub.card.unified.desc":
      "Search laws, requirements, tariffs by HS code / product / domain.",
    "hub.card.precedents.title": "KCS Tariff Classification Cases",
    "hub.card.precedents.desc":
      "12,606 cases hybrid search (pgroonga FTS + pgvector semantic). Bookmarks, CSV, synonym auto expansion.",
    "hub.card.synonyms.title": "Synonyms Dictionary",
    "hub.card.synonyms.desc":
      "FTS synonyms 200+ CRUD. Domain vocabulary for customs brokers. Bidirectional term ↔ synonym.",
    "hub.card.overview.title": "📖 System Overview",
    "hub.card.overview.desc":
      "Ontology-based HS Classification — 4 Layer architecture, search flow, key features, use cases.",
    "hub.card.tradePartners.title": "Customs Brokers Ontology",
    "hub.card.tradePartners.desc":
      "5 group customs history. Per-party HS distribution + specificity. Source of AI precision-mode prior.",
    "hub.card.ontoSearch.title": "Ontology Search + Narrowing",
    "hub.card.ontoSearch.desc":
      "line_item keyword → unified HS candidates → narrow chips (material/form/use). 5 group unified 5M+ rows.",
    "hub.card.history.title": "Classification History",
    "hub.card.history.desc":
      "User corrections + weak spots + statistics. Source for training data.",
    "hub.footer":
      "Click a card to enter sub-page. Sidebar shows single 'HS Classification' entry.",

    // SynonymsPage
    "synonyms.title": "Synonyms Dictionary",
    "synonyms.total": "{n} ({total} total)",
    "synonyms.search.placeholder": "term / synonym partial match",
    "synonyms.domain.all": "(All domains)",
    "synonyms.domain.title": "Domain filter",
    "synonyms.add.title": "Add new synonym",
    "synonyms.empty": "No results",
    "synonyms.col.term": "term",
    "synonyms.col.synonym": "synonym",
    "synonyms.col.note": "note",
    "synonyms.uncategorized": "(uncategorized)",
    "synonyms.required": "term + synonym both required",
    "synonyms.delete.confirm":
      'Delete: "{term}" ↔ "{synonym}" (id={id})\nProceed?',
    "synonyms.draft.term.ph": "term (Korean/English)",
    "synonyms.draft.synonym.ph": "synonym (opposite form)",
    "synonyms.draft.domain.ph": "domain (e.g.: 42-leather)",
    "synonyms.draft.note.ph": "note (optional)",
  },
};

function detectInitialLang() {
  if (typeof window === "undefined") return "ko";
  try {
    const saved = localStorage.getItem(LANG_KEY);
    if (saved && SUPPORTED.includes(saved)) return saved;
  } catch (_) {
    /* ignore */
  }
  // navigator.language fallback
  const nav =
    typeof navigator !== "undefined"
      ? (navigator.language || "").toLowerCase()
      : "";
  return nav.startsWith("ko") ? "ko" : "en";
}

let _globalLang = detectInitialLang();
const _listeners = new Set();

export function getLang() {
  return _globalLang;
}

export function setLangGlobal(next) {
  if (!SUPPORTED.includes(next)) return;
  _globalLang = next;
  try {
    localStorage.setItem(LANG_KEY, next);
  } catch (_) {
    /* ignore */
  }
  _listeners.forEach((cb) => cb(next));
}

/** t(key, params?) — params {n: 4} → "{n}건" 치환 */
export function t(key, params) {
  const dict = messages[_globalLang] || messages.ko;
  let s = dict[key] ?? messages.ko[key] ?? key;
  if (params && typeof s === "string") {
    Object.entries(params).forEach(([k, v]) => {
      s = s.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
    });
  }
  return s;
}

/** React hook — language state + setLang. */
export function useLang() {
  const [lang, setLangState] = useState(_globalLang);

  useEffect(() => {
    const cb = (next) => setLangState(next);
    _listeners.add(cb);
    return () => {
      _listeners.delete(cb);
    };
  }, []);

  const setLang = useCallback((next) => {
    setLangGlobal(next);
  }, []);

  const tBound = useCallback(
    (key, params) => {
      // lang 변경에 반응하기 위해 closure 가 아닌 모듈 전역 참조
      const dict = messages[lang] || messages.ko;
      let s = dict[key] ?? messages.ko[key] ?? key;
      if (params && typeof s === "string") {
        Object.entries(params).forEach(([k, v]) => {
          s = s.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
        });
      }
      return s;
    },
    [lang],
  );

  return { lang, setLang, t: tBound, supported: SUPPORTED };
}
